import { useCallback, useState } from 'react';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import InfoButton from '@perpay-web/components/base/InfoButton/InfoButton';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Inline from '@perpay-web/components/design-system/Inline/Inline';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import approvedCardSVG from '@perpay-web/fintech/assets/images/approvedCard.svg';
import approvedCardSilverPNG from '@perpay-web/fintech/assets/images/approvedCardSilver.png';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CardDepositInfoModal from '@perpay-web/fintech/components/base/CardDepositInfoModal/CardDepositInfoModal';
import Confetti from '@perpay-web/fintech/components/base/Confetti/Confetti';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import Stepper from '@perpay-web/fintech/components/base/Stepper/Stepper';
import {
    cardApplicationStepperNames,
    OFFER_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { cardFees } from '@perpay-web/fintech/constants/cardFeesConstants';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { useMount } from '@perpay-web/hooks/useMount';
import { getClassName } from '@perpay-web/utils/domUtils';
import { noop } from '@perpay-web/utils/noop';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './CardApplicationApproved.scss';

const CardApplicationApproved = ({
    estimatedCardMinimumPayment,
    creditLimit,
    apr,
    monthlyFee,
    metalCardFee,
    isMetalOrSilverCard,
    onClickAccept,
    onClickNotNow,
    onOpenInfoModal,
    onCloseInfoModal,
    partnerCode,
    welcomeOffer,
    dashboardURL = paths.dashboard.path,
    onMount = noop,
    isLoading = false,
    isUserInPremiumGroup = false,
}) => {
    const renderHeaderCb = useCallback(() => <div />, []);
    useFullHeader(renderHeaderCb);

    const creditLimitString = formatCurrency(creditLimit, true, false);
    const isLcllf = creditLimit < 50000;
    const monthlyFeeString = formatCurrency(
        isLcllf ? cardFees.LCLLF_MONTHLY_FEE : monthlyFee,
        true,
        isLcllf,
    );
    const metalCardFeeString = formatCurrency(metalCardFee, true, false);
    const minimumPaymentKeepCents = estimatedCardMinimumPayment % 100 !== 0;
    const estimatedCardMinimumPaymentString = formatCurrency(
        estimatedCardMinimumPayment,
        true,
        minimumPaymentKeepCents,
    );
    const { isModalOpen, setIsModalOpen } = useModal();
    const {
        isAnimatedModalOpen,
        setIsAnimatedModalOpen,
        animatedModalClassName,
    } = useAnimatedModal({ isModalOpen, setIsModalOpen });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const openInfoModal = useCallback(() => {
        setIsAnimatedModalOpen(true);
        onOpenInfoModal();
    }, [onOpenInfoModal, setIsAnimatedModalOpen]);

    const closeInfoModal = useCallback(() => {
        setIsAnimatedModalOpen(false);
        onCloseInfoModal();
    }, [onCloseInfoModal, setIsAnimatedModalOpen]);

    const renderStepper = () => {
        const currentStep = cardApplicationStepperNames.findIndex(
            (step) => step.step === OFFER_STEP,
        );

        return (
            <Stepper
                steps={cardApplicationStepperNames}
                currentStep={currentStep}
                className={getClassName(
                    styles.stepper,
                    welcomeOffer ? styles.stepper__offer : '',
                )}
            />
        );
    };

    const getCardImage = () => {
        if (isUserInPremiumGroup && isMetalOrSilverCard) {
            return approvedCardSilverPNG;
        }
        return approvedCardSVG;
    };

    const renderWelcomeOffer = () =>
        welcomeOffer ? (
            <div className={styles['welcome-offer']}>
                ${welcomeOffer} Welcome Bonus
            </div>
        ) : null;

    const renderFullHeaderCb = useCallback(
        () => <CardApplicationHeader partnerCode={partnerCode} />,
        [partnerCode],
    );

    useFullHeader(renderFullHeaderCb);

    useMount(() => {
        onMount();
    });

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    return (
        <FocusLayout className={styles.container}>
            <Confetti className={styles.confetti} />
            {renderWelcomeOffer()}
            {renderStepper()}
            <FocusContent className={styles.content}>
                <div
                    className={getClassName(
                        isUserInPremiumGroup && isMetalOrSilverCard
                            ? styles['img__wrapper--no-bg']
                            : styles.img__wrapper,
                    )}
                >
                    <img
                        src={getCardImage()}
                        className={getClassName(
                            styles.approved__img,
                            isUserInPremiumGroup && isMetalOrSilverCard
                                ? styles['approved__img--premium']
                                : '',
                        )}
                        alt='Perpay Credit Card'
                    />
                </div>
                <div className={styles.approved__wrapper}>
                    <div className={styles.approved__title}>
                        You are approved!
                    </div>
                    <div className={styles.approved__credit}>
                        Starting Credit Limit
                    </div>
                    <div className={styles['approved__credit-amount']}>
                        {creditLimitString}
                    </div>
                    <div className={styles.approved__note}>
                        With consistent on time payments this number can
                        increase in as little as 3 months!
                    </div>
                </div>
                <Stack className={styles.details}>
                    <Inline
                        className={getClassName(
                            styles.details__items,
                            isMetalOrSilverCard
                                ? styles['details__items--metal']
                                : '',
                        )}
                    >
                        <div className={styles.details__item}>
                            <div className={styles['details__item-title']}>
                                Variable APR
                            </div>
                            <div className={styles['details__item-amount']}>
                                {apr}%
                            </div>
                        </div>

                        <div className={styles.details__item}>
                            <div className={styles['details__item-title']}>
                                Monthly fee
                            </div>
                            <div className={styles['details__item-amount']}>
                                {monthlyFeeString}
                            </div>
                        </div>

                        {isMetalOrSilverCard ? (
                            <div className={styles.details__item}>
                                <div className={styles['details__item-title']}>
                                    Card fee
                                </div>
                                <div className={styles['details__item-amount']}>
                                    {metalCardFeeString}
                                </div>
                            </div>
                        ) : null}
                    </Inline>
                    <Inline>
                        <div className={styles.details__item}>
                            <div className={styles['details__item-row']}>
                                <div className={styles['details__item-title']}>
                                    Card minimum payroll direct deposit
                                </div>
                                <InfoButton
                                    className={styles['details__info-icon']}
                                    onClick={openInfoModal}
                                />
                            </div>
                            <div className={styles['details__item-amount']}>
                                {estimatedCardMinimumPaymentString}/pay
                            </div>
                        </div>
                    </Inline>
                    {isAnimatedModalOpen ? (
                        <CardDepositInfoModal
                            onClose={closeInfoModal}
                            className={animatedModalClassName}
                        />
                    ) : null}
                </Stack>
            </FocusContent>
            <FocusFooter className={styles.footer}>
                <div className={styles.footer__copy}>
                    By clicking &lsquo;Accept & Continue&rsquo; you agree to the
                    offer above.
                </div>
                <CardCta
                    Component={CustomButton}
                    mode='dark'
                    className={styles.footer__button}
                    onClick={() => {
                        setIsSubmitting(true);
                        onClickAccept();
                    }}
                    loading={isSubmitting}
                >
                    Accept &amp; Continue
                </CardCta>
                <CustomLink
                    data-chromatic='ignore'
                    href={
                        partnerCode
                            ? `${dashboardURL}?PHO_complete=true`
                            : dashboardURL
                    }
                    className={styles['footer__skip-link']}
                    onClick={onClickNotNow}
                >
                    Not right now
                </CustomLink>
            </FocusFooter>
        </FocusLayout>
    );
};

export default CardApplicationApproved;
